// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState } from 'react';
import './VolumeSparkline.css';

/**
 * SVG sparkline showing daily submission volume over the selected window.
 * Fills all days in the range (including zeroes) so the x-axis is uniform.
 */
export default function VolumeSparkline({ data, days }) {
  const [tooltip, setTooltip] = useState(null);

  // Build a complete day range with zeroes for missing days
  const allDays = buildDayRange(days);
  const countByDay = Object.fromEntries(data.map((d) => [d.date, d.count]));
  const points = allDays.map((date) => ({ date, count: countByDay[date] || 0 }));

  const maxCount = Math.max(...points.map((p) => p.count), 1);
  const totalInPeriod = points.reduce((s, p) => s + p.count, 0);

  const W = 340, H = 100;
  const PAD = { top: 10, right: 10, bottom: 24, left: 28 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const n = points.length;
  const xScale = (i) => PAD.left + (i / Math.max(n - 1, 1)) * innerW;
  const yScale = (v) => PAD.top + innerH - (v / maxCount) * innerH;

  // Build polyline path
  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(i).toFixed(1)},${yScale(p.count).toFixed(1)}`)
    .join(' ');

  // Area fill
  const areaPath = [
    linePath,
    `L${xScale(n - 1).toFixed(1)},${(PAD.top + innerH).toFixed(1)}`,
    `L${PAD.left.toFixed(1)},${(PAD.top + innerH).toFixed(1)}`,
    'Z',
  ].join(' ');

  // Y-axis ticks
  const yTicks = [0, Math.ceil(maxCount / 2), maxCount];

  // X-axis: show ~4 evenly spaced date labels
  const xLabelIndices = [0, Math.floor(n / 3), Math.floor((2 * n) / 3), n - 1].filter(
    (i, idx, arr) => arr.indexOf(i) === idx && i < n
  );

  return (
    <div className="sparkline">
      <div className="sparkline__total">
        <strong>{totalInPeriod}</strong> submissions in the last {days} days
      </div>
      <div className="sparkline__wrap">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label={`Daily submission volume over the last ${days} days`}
          style={{ display: 'block' }}
        >
          <defs>
            <linearGradient id="area-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--green-light)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="var(--green-light)" stopOpacity="0.03" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {yTicks.map((t) => (
            <line
              key={t}
              x1={PAD.left}
              x2={W - PAD.right}
              y1={yScale(t)}
              y2={yScale(t)}
              stroke="var(--gray-200)"
              strokeWidth="1"
              strokeDasharray={t === 0 ? '0' : '3 3'}
            />
          ))}

          {/* Y-axis labels */}
          {yTicks.map((t) => (
            <text
              key={t}
              x={PAD.left - 4}
              y={yScale(t) + 4}
              textAnchor="end"
              fontSize="9"
              fill="var(--gray-400)"
            >
              {t}
            </text>
          ))}

          {/* Area fill */}
          <path d={areaPath} fill="url(#area-fill)" />

          {/* Line */}
          <path d={linePath} fill="none" stroke="var(--green-light)" strokeWidth="2" strokeLinejoin="round" />

          {/* Data points (interactive) */}
          {points.map((p, i) => (
            <circle
              key={p.date}
              cx={xScale(i)}
              cy={yScale(p.count)}
              r={tooltip?.index === i ? 5 : 3}
              fill={p.count > 0 ? 'var(--green-mid)' : 'transparent'}
              stroke={p.count > 0 ? 'var(--white)' : 'transparent'}
              strokeWidth="1.5"
              style={{ cursor: 'pointer', transition: 'r 0.1s' }}
              onMouseEnter={() => setTooltip({ index: i, x: xScale(i), y: yScale(p.count), ...p })}
              onMouseLeave={() => setTooltip(null)}
              aria-label={`${p.date}: ${p.count} submission${p.count !== 1 ? 's' : ''}`}
              role="graphics-symbol"
            />
          ))}

          {/* Tooltip */}
          {tooltip && (
            <g>
              <rect
                x={Math.min(tooltip.x - 30, W - PAD.right - 60)}
                y={tooltip.y - 32}
                width={60}
                height={24}
                rx={4}
                fill="var(--green-dark)"
                opacity="0.9"
              />
              <text
                x={Math.min(tooltip.x, W - PAD.right - 30)}
                y={tooltip.y - 17}
                textAnchor="middle"
                fontSize="9"
                fill="white"
              >
                {formatDate(tooltip.date)}
              </text>
              <text
                x={Math.min(tooltip.x, W - PAD.right - 30)}
                y={tooltip.y - 7}
                textAnchor="middle"
                fontSize="10"
                fontWeight="bold"
                fill="white"
              >
                {tooltip.count}
              </text>
            </g>
          )}

          {/* X-axis labels */}
          {xLabelIndices.map((i) => (
            <text
              key={i}
              x={xScale(i)}
              y={H - 4}
              textAnchor="middle"
              fontSize="9"
              fill="var(--gray-400)"
            >
              {formatDateShort(points[i].date)}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
}

function buildDayRange(days) {
  const result = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    result.push(d.toISOString().substring(0, 10));
  }
  return result;
}

function formatDate(iso) {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${parseInt(m)}/${parseInt(d)}`;
}

function formatDateShort(iso) {
  return formatDate(iso);
}
